'use client';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import noop from 'lodash/noop';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { ConnectedFocusError } from 'focus-formik-error';

import '@utils/forms/phoneValidation';
import { PhoneInput, Input, Checkbox, ErrorState } from '@components/Form';
import Button from '@repo/ui/src/components/Button';
import { useSignup } from '@hooks/auth';

import {
  AuthViews,
  initialValues,
  InputKeys,
  PASSWORD_REGEX,
} from '@repo/ui/src/modules/UI/constants';
import LoadingLogo from '@components/Loading/LoadingLogo';

interface Props {
  onSuccess?: () => Promise<void>;
  isCheckout?: boolean;
}

const CreateUser = ({ onSuccess = noop as any, isCheckout = false }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useSignup();

  const onSubmit = async (
    values: typeof initialValues.create,
    { setStatus, resetForm }: FormikHelpers<typeof initialValues.create>,
  ) => {
    setStatus(null);
    try {
      if (!values.terms) {
        return setStatus(
          t('auth.fields.termsOfService.requiredText', undefined, {
            default: "You must agree to Traeger's terms and conditions.",
          }),
        );
      }

      await trigger(values);
      await onSuccess();
      resetForm();
    } catch (e: any) {
      setStatus(e?.response?.data?.message);
    }
  };

  const validationSchema = () =>
    Yup.object().shape({
      [InputKeys.FIRST_NAME]: Yup.string().required(
        t('auth.fields.firstNameInput.requiredText', undefined, {
          default: 'First name is required.',
        }),
      ),
      [InputKeys.LAST_NAME]: Yup.string().required(
        t('auth.fields.lastNameInput.requiredText', undefined, {
          default: 'Last name is required.',
        }),
      ),
      [InputKeys.EMAIL]: Yup.string()
        .email(
          t('auth.fields.emailInput.invalidText', undefined, {
            default: 'Invalid email.',
          }),
        )
        .required(
          t('auth.fields.emailInput.requiredText', undefined, {
            default: 'Email is required.',
          }),
        ),
      [InputKeys.PHONE_NUMBER]: Yup.string()
        .phone()
        .when(InputKeys.MARKETING_SMS, {
          is: true,
          then: Yup.string().required(
            t('auth.fields.phoneInput.requiredText', undefined, {
              default: 'Phone number is required.',
            }),
          ),
        }),
      [InputKeys.PASSWORD]: Yup.string()
        .required(
          t('auth.fields.passwordInput.requiredText', undefined, {
            default: 'Password is required.',
          }),
        )
        .matches(
          PASSWORD_REGEX,
          t('auth.fields.passwordInput.invalidText', undefined, {
            default: 'Password is Invalid.',
          }),
        ),
      [InputKeys.PASSWORD_CONFIRMATION]: Yup.string()
        .required(
          t('auth.fields.passwordConfirmationInput.requiredText', undefined, {
            default: 'Password confirmation is required.',
          }),
        )
        .oneOf(
          [Yup.ref(InputKeys.PASSWORD), null],
          t('auth.fields.passwordConfirmationInput.invalidText', undefined, {
            default: 'Passwords must match.',
          }),
        ),
    });

  return (
    <Formik
      validationSchema={validationSchema}
      validateOnChange={false}
      onSubmit={onSubmit}
      initialValues={initialValues[AuthViews.CREATE]}
    >
      {({ isSubmitting, status }) => (
        <Form className="tw-mb-7 tw-grid tw-gap-y-5 sm:tw-gap-y-7">
          <ConnectedFocusError />

          <div className="tw-grid md:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-5 sm:tw-gap-y-7">
            <Input
              required
              id="firstName"
              name="firstName"
              autoComplete="given-name"
              label={t('auth.fields.firstNameInput.label', undefined, { default: 'First Name' })}
            />
            <Input
              required
              autoComplete="family-name"
              id="lastName"
              name="lastName"
              label={t('auth.fields.lastNameInput.label', undefined, { default: 'Last Name' })}
            />
          </div>
          <Input
            required
            autoComplete="email"
            id={InputKeys.EMAIL}
            name={InputKeys.EMAIL}
            type="email"
            label={t('auth.fields.emailInput.label', undefined, { default: 'Email' })}
          />
          <PhoneInput
            autoComplete="tel-national"
            id="phoneNumber"
            name="phoneNumber"
            type="text"
            label={t('auth.fields.phoneInput.label', undefined, { default: 'Phone Number' })}
          />
          <Input
            required
            autoComplete="new-password"
            id="password"
            name="password"
            type="password"
            label={t('auth.fields.passwordInput.label', undefined, { default: 'Password' })}
          />
          <Input
            required
            autoComplete="current-password"
            id="passwordConfirmation"
            name="passwordConfirmation"
            type="password"
            label={t('auth.fields.passwordConfirmationInput.label', undefined, {
              default: 'Password Confirmation',
            })}
          />
          <Checkbox
            id="emailSubscribe"
            name="emailSubscribe"
            label={t('auth.create.form.emailSubscribeLabel', undefined, {
              default: 'Sign me up for Traeger Marketing Emails',
            })}
          />
          <Checkbox
            id="smsSubscribe"
            name="smsSubscribe"
            label={t('auth.create.form.smsSubscribeLabel', undefined, {
              default: 'Sign me up for Traeger SMS Texts',
            })}
          />
          <Checkbox
            id="terms"
            name="terms"
            label={t('auth.create.form.termsAndConditionsLabel', undefined, {
              default: "I agree to Traeger's terms and conditions",
            })}
          />
          <ErrorState text={status} />
          <Button size="large" disabled={isSubmitting} type="submit" full>
            {isSubmitting ? (
              <LoadingLogo small />
            ) : isCheckout ? (
              t('auth.create.form.buttonTextCheckout', undefined, {
                default: 'Create Account and Checkout',
              })
            ) : (
              t('auth.create.form.buttonText', undefined, { default: 'Create Account' })
            )}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default CreateUser;
